import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
    IconPaperclip, IconX, IconAlertTriangle, IconLoader2, IconCircleCheck, IconInfoCircle,
} from '@tabler/icons-react';
import { useAuth } from '../../contexts/AuthContext';
import Breadcrumb from '../../components/common/Breadcrumb';
import { PageLoader } from '../../components/common/Loader';
import { useMyProfile } from '../../hooks/useEmployees';
import { useTicketOptions, useCreateTicket } from '../../hooks/useSupportTickets';
import {
    PRIORITY_CRITERIA, classifyWorkflowError, withoutEmpty, isForbidden,
    ITS_ACT_PERMISSIONS,
} from './utils';

const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

// Guidance for the description box, not six separate fields.
const DESCRIPTION_HINTS = [
    'What happened, and what did you expect instead?',
    'When did it start, and does it happen every time?',
    'The exact error message, if there is one.',
    'What you have already tried.',
];

function Field({ label, required, error, hint, children }) {
    return (
        <div className="form-group">
            <label className="block text-sm font-medium mb-5">
                {label} {required && <span className="text-danger">*</span>}
            </label>
            {children}
            {hint && <p className="text-xs text-text-secondary mt-5">{hint}</p>}
            {error && <p className="text-danger text-xs mt-5">{error}</p>}
        </div>
    );
}

function Select({ value, onChange, options = [], placeholder = 'Select...', error }) {
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className={`w-full bg-bg-light border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none ${error ? 'border-danger' : 'border-border'}`}
        >
            <option value="">{placeholder}</option>
            {options.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
            ))}
        </select>
    );
}

const textInputClass = (error) =>
    `w-full bg-bg-light border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none ${error ? 'border-danger' : 'border-border'}`;

// Wording is close to verbatim from the paper form.
function SubmittedPanel({ ticket, onNewTicket, onView }) {
    return (
        <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
            <div className="flex flex-col items-center text-center py-20">
                <div className="w-60 h-60 rounded-full bg-success/10 flex items-center justify-center mb-20">
                    <IconCircleCheck size={32} className="text-success" />
                </div>
                <h3 className="text-xl font-semibold mb-10">Request submitted</h3>
                <p className="text-2xl font-bold text-primary tracking-wide mb-15">{ticket.reference_no}</p>
                <p className="text-sm text-text-secondary max-w-[560px]">
                    Your IT support request has been submitted successfully. The IT Support team will
                    review your request, validate its priority, and contact you using your preferred
                    communication method. Please keep your reference number for follow-up. Do not
                    submit multiple forms for the same issue unless requested.
                </p>
                <div className="flex gap-10 mt-20">
                    <button onClick={onView} className="btn btn-primary">View Ticket</button>
                    <button onClick={onNewTicket} className="btn btn-outline-secondary">Raise Another</button>
                </div>
            </div>
        </div>
    );
}

export default function SupportTicketNew() {
    const navigate = useNavigate();
    const { user, hasAnyPermission } = useAuth();
    const orgId = user?.orgId;
    const fileInputRef = useRef(null);

    const canAct = hasAnyPermission(ITS_ACT_PERMISSIONS);

    const { data: options, isLoading: loadingOptions, error: optionsError } = useTicketOptions();
    const { data: profile } = useMyProfile();
    const createTicket = useCreateTicket();

    const [form, setForm] = useState({
        phone: '',
        office_location: '',
        preferred_contact: '',
        request_type: '',
        affected_system: '',
        asset_tag: '',
        summary: '',
        description: '',
        users_affected: '',
        work_impact: '',
        requested_priority: '',
        business_justification: '',
        has_workaround: '',
        troubleshooting_availability: '',
        required_completion_date: '',
    });
    const [attachments, setAttachments] = useState([]);
    const [errors, setErrors] = useState({});
    const [submitted, setSubmitted] = useState(null);

    // Pre-filled from the employee record; API fills blanks anyway.
    useEffect(() => {
        if (!profile) return;
        setForm(prev => ({
            ...prev,
            phone: prev.phone || profile.phone_number || profile.phone || '',
            office_location: prev.office_location || profile.office_location || profile.location || '',
        }));
    }, [profile]);

    const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    // The one piece of conditional logic the options payload drives.
    const justificationRequired = useMemo(() => {
        const requiredFor = options?.justification_required_for || [];
        return requiredFor.includes(form.requested_priority);
    }, [options, form.requested_priority]);

    const handleFiles = (e) => {
        const picked = Array.from(e.target.files || []);
        const tooBig = picked.filter(f => f.size > MAX_ATTACHMENT_BYTES);
        if (tooBig.length) {
            toast.error(`${tooBig.map(f => f.name).join(', ')} exceeds the 10 MB limit.`);
        }
        setAttachments(prev => [...prev, ...picked.filter(f => f.size <= MAX_ATTACHMENT_BYTES)]);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const removeAttachment = (index) => setAttachments(prev => prev.filter((_, i) => i !== index));

    const handleSubmit = (e) => {
        e.preventDefault();
        const nextErrors = {};

        if (!form.request_type) nextErrors.request_type = 'Choose the type of request.';
        if (!form.summary.trim()) nextErrors.summary = 'A short summary is required.';
        if (!form.description.trim()) nextErrors.description = 'Please describe the issue.';
        if (!form.requested_priority) nextErrors.requested_priority = 'Choose a priority.';
        // Enforced client-side too, or the submit comes back 400.
        if (justificationRequired && !form.business_justification.trim()) {
            nextErrors.business_justification = 'This field is required for High and Critical priority requests.';
        }

        if (Object.keys(nextErrors).length) {
            setErrors(nextErrors);
            toast.error('Please correct the highlighted fields.');
            return;
        }

        setErrors({});
        createTicket.mutate(
            { data: withoutEmpty(form), attachments, orgId },
            {
                onSuccess: (res) => setSubmitted(res),
                onError: (err) => {
                    const { fieldErrors, message } = classifyWorkflowError(err);
                    setErrors(fieldErrors);
                    toast.error(message);
                },
            }
        );
    };

    if (!canAct) {
        return (
            <div className="flex flex-col min-h-screen gap-20">
                <Breadcrumb title="New Support Ticket" path="Support / New Ticket" />
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 text-center text-text-secondary">
                    You do not have permission to raise IT support tickets.
                </div>
            </div>
        );
    }

    if (loadingOptions) return <PageLoader />;

    if (isForbidden(optionsError) || (!options && optionsError)) {
        return (
            <div className="flex flex-col min-h-screen gap-20">
                <Breadcrumb title="New Support Ticket" path="Support / New Ticket" />
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 text-center text-text-secondary">
                    The support request form could not be loaded. Please try again shortly.
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className="flex flex-col min-h-screen gap-20">
                <Breadcrumb title="New Support Ticket" path="Support / New Ticket" />
                <SubmittedPanel
                    ticket={submitted}
                    onView={() => navigate(`/support/tickets/${submitted.id}`)}
                    onNewTicket={() => { setSubmitted(null); setForm(prev => ({ ...prev, summary: '', description: '', asset_tag: '', affected_system: '' })); setAttachments([]); }}
                />
            </div>
        );
    }

    return (
        <div className="flex flex-col min-h-screen gap-20">
            <Breadcrumb title="New Support Ticket" path="Support / New Ticket" />

            <form onSubmit={handleSubmit} className="flex flex-col gap-20">
                {/* Contact */}
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                    <h3 className="text-base font-semibold mb-15">Your Contact Details</h3>
                    <p className="text-xs text-text-secondary mb-15">
                        Pre-filled from your employee record — correct anything that is out of date.
                    </p>
                    <div className="grid md:grid-cols-3 grid-cols-1 gap-15">
                        <Field label="Phone" error={errors.phone}>
                            <input type="text" value={form.phone} onChange={(e) => setField('phone', e.target.value)}
                                className={textInputClass(errors.phone)} placeholder="+234..." />
                        </Field>
                        <Field label="Office Location" error={errors.office_location}>
                            <input type="text" value={form.office_location} onChange={(e) => setField('office_location', e.target.value)}
                                className={textInputClass(errors.office_location)} placeholder="e.g. Kano Office" />
                        </Field>
                        <Field label="Preferred Contact Method" error={errors.preferred_contact}>
                            <Select value={form.preferred_contact} onChange={(v) => setField('preferred_contact', v)}
                                options={options?.preferred_contact} error={errors.preferred_contact} />
                        </Field>
                    </div>
                </div>

                {/* The issue */}
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                    <h3 className="text-base font-semibold mb-15">About the Issue</h3>
                    <div className="flex flex-col gap-15">
                        <div className="grid md:grid-cols-2 grid-cols-1 gap-15">
                            <Field label="Type of Request" required error={errors.request_type}>
                                <Select value={form.request_type} onChange={(v) => setField('request_type', v)}
                                    options={options?.request_type} error={errors.request_type} />
                            </Field>
                            <Field label="Affected System or Service" error={errors.affected_system}>
                                <input type="text" value={form.affected_system} onChange={(e) => setField('affected_system', e.target.value)}
                                    className={textInputClass(errors.affected_system)} placeholder="e.g. Office Wi-Fi" />
                            </Field>
                        </div>

                        <Field label="Short Summary" required error={errors.summary}>
                            <input type="text" value={form.summary} onChange={(e) => setField('summary', e.target.value)}
                                className={textInputClass(errors.summary)}
                                placeholder="One line — e.g. Unable to connect to the office Wi-Fi" />
                        </Field>

                        <Field label="Detailed Description" required error={errors.description}>
                            <textarea value={form.description} onChange={(e) => setField('description', e.target.value)} rows={6}
                                className={`${textInputClass(errors.description)} resize-none`}
                                placeholder="Describe the problem in as much detail as you can." />
                            <ul className="text-xs text-text-secondary mt-8 list-disc pl-15 flex flex-col gap-2">
                                {DESCRIPTION_HINTS.map(hint => <li key={hint}>{hint}</li>)}
                            </ul>
                        </Field>

                        <div className="grid md:grid-cols-2 grid-cols-1 gap-15">
                            <Field label="Asset Tag" error={errors.asset_tag} hint="On the sticker on your device, if relevant.">
                                <input type="text" value={form.asset_tag} onChange={(e) => setField('asset_tag', e.target.value)}
                                    className={textInputClass(errors.asset_tag)} placeholder="HSCL-LT-0231" />
                            </Field>
                            <Field label="Is there a workaround?" error={errors.has_workaround}>
                                <Select value={form.has_workaround} onChange={(v) => setField('has_workaround', v)}
                                    options={options?.has_workaround} error={errors.has_workaround} />
                            </Field>
                        </div>
                    </div>
                </div>

                {/* Impact and priority */}
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                    <h3 className="text-base font-semibold mb-15">Impact and Priority</h3>
                    <div className="flex flex-col gap-15">
                        <div className="grid md:grid-cols-3 grid-cols-1 gap-15">
                            <Field label="How many people are affected?" error={errors.users_affected}>
                                <Select value={form.users_affected} onChange={(v) => setField('users_affected', v)}
                                    options={options?.users_affected} error={errors.users_affected} />
                            </Field>
                            <Field label="Impact on your work" error={errors.work_impact}>
                                <Select value={form.work_impact} onChange={(v) => setField('work_impact', v)}
                                    options={options?.work_impact} error={errors.work_impact} />
                            </Field>
                            <Field label="Requested Priority" required error={errors.requested_priority}>
                                <Select value={form.requested_priority} onChange={(v) => setField('requested_priority', v)}
                                    options={options?.requested_priority} error={errors.requested_priority} />
                            </Field>
                        </div>

                        <div className="bg-bg-light border border-border rounded-[4px] p-15">
                            <p className="text-xs font-medium mb-8 flex items-center gap-5">
                                <IconInfoCircle size={14} /> How to choose a priority
                            </p>
                            <ul className="text-xs text-text-secondary flex flex-col gap-3">
                                {Object.entries(PRIORITY_CRITERIA).map(([key, text]) => (
                                    <li key={key}><span className="font-medium text-text-main">{key}</span> — {text}</li>
                                ))}
                            </ul>
                            <p className="text-xs text-text-secondary mt-8 italic">
                                IT may adjust the priority after triage — you will be told why if they do.
                            </p>
                        </div>

                        {justificationRequired && (
                            <Field
                                label="Business Justification"
                                required
                                error={errors.business_justification}
                                hint="Required for High and Critical priority requests."
                            >
                                <textarea value={form.business_justification} onChange={(e) => setField('business_justification', e.target.value)} rows={3}
                                    className={`${textInputClass(errors.business_justification)} resize-none`}
                                    placeholder="Why does this need to be treated as high priority?" />
                            </Field>
                        )}

                        <div className="grid md:grid-cols-2 grid-cols-1 gap-15">
                            <Field label="When are you available for troubleshooting?" error={errors.troubleshooting_availability}>
                                <input type="text" value={form.troubleshooting_availability} onChange={(e) => setField('troubleshooting_availability', e.target.value)}
                                    className={textInputClass(errors.troubleshooting_availability)} placeholder="e.g. Weekdays after 2pm" />
                            </Field>
                            <Field label="Required Completion Date" error={errors.required_completion_date}>
                                <input type="date" value={form.required_completion_date} onChange={(e) => setField('required_completion_date', e.target.value)}
                                    className={textInputClass(errors.required_completion_date)} />
                            </Field>
                        </div>
                    </div>
                </div>

                {/* Attachments */}
                <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
                    <h3 className="text-base font-semibold mb-15">Attachments</h3>

                    <div className="bg-danger/10 border border-danger/30 rounded-[4px] px-15 py-10 mb-15 flex items-start gap-8">
                        <IconAlertTriangle size={16} className="text-danger mt-2 shrink-0" />
                        <p className="text-sm text-danger">
                            Do not upload passwords or authentication details. Screenshots of a login
                            page are fine; screenshots containing credentials are not.
                        </p>
                    </div>

                    <input ref={fileInputRef} type="file" multiple onChange={handleFiles}
                        accept="image/*,application/pdf,.docx,.txt" className="hidden" />
                    <button type="button" onClick={() => fileInputRef.current?.click()}
                        className="btn btn-outline-secondary flex items-center gap-5">
                        <IconPaperclip size={16} /> Add files
                    </button>
                    <p className="text-xs text-text-secondary mt-8">
                        Images, PDF, DOCX and TXT. Maximum 10 MB per file.
                    </p>

                    {attachments.length > 0 && (
                        <div className="flex flex-col gap-8 mt-15">
                            {attachments.map((file, index) => (
                                <div key={`${file.name}-${index}`}
                                    className="flex items-center justify-between gap-10 bg-bg-light border border-border rounded-[4px] px-10 py-[8px]">
                                    <span className="text-sm truncate">{file.name}</span>
                                    <button type="button" onClick={() => removeAttachment(index)}
                                        className="text-text-secondary hover:text-danger shrink-0">
                                        <IconX size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex gap-10 justify-end">
                    <button type="button" onClick={() => navigate('/support/tickets')}
                        className="btn btn-outline-secondary" disabled={createTicket.isPending}>
                        Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={createTicket.isPending}>
                        {createTicket.isPending ? (
                            <span className="flex items-center gap-5">
                                <IconLoader2 size={14} className="animate-spin" /> Submitting...
                            </span>
                        ) : 'Submit Request'}
                    </button>
                </div>
            </form>
        </div>
    );
}
